import type { Dispatch, AnyAction } from 'redux';

import * as ContactCreateAPI from 'ab-api/web/contact/create';
import * as EditContactAPI from 'ab-api/web/contact/update';

import type ContactVM from 'ab-viewModels/contact/contact';
import type { ContactForInvoicesVM } from 'ab-viewModels/contact/contactsForInvoices.viewModel';
import type { ContactNotificationHistoryTableVM } from 'ab-viewModels/contact/notifiactionHistoryTable.viewModel';
import type { CompanyContactTableVM, CompanyUnsavedContactTableVM } from 'ab-viewModels/contact/table.viewModel';
import type { BulkCreateResponseViewModel } from 'ab-viewModels/csv.viewModel';

import type ImportContactRM from 'ab-requestModels/contact/import';
import type MergeContactRM from 'ab-requestModels/contact/merge';

import type { GetRootState } from 'af-reducers';

import { errorHandler } from 'af-utils/actions.util';
import { http } from 'af-utils/http.util';

import API from 'af-constants/routes/api';
import CLIENT from 'af-constants/routes/client';

import type { TableContent } from 'ab-common/dataStructures/tableContent';
import { TableQuery } from 'ab-common/dataStructures/tableQuery';

export function bulkSoftDeleteContact(contactIds: number[]) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {
		const action = async () => {
			await http.delete(API.V1.CONTACT.BULK_SOFT_DELETE, { contactIds });
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function importBatch(data: ImportContactRM[]) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {
		const action = async () => {
			return await http.post<BulkCreateResponseViewModel<ImportContactRM>>(API.V1.CONTACT.IMPORT, data);
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function createContact(form: ContactCreateAPI.W_Contact_Create_RM) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.post<ContactCreateAPI.W_Contact_Create_VM>(ContactCreateAPI.URL(), form);
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findById(id: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {
		const action = async () => {
			return await http.get<ContactVM>(API.V1.CONTACT.FIND_BY_ID(id));
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findNotificationHistory(id: number, tableRequestModel: TableQuery) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {
		const action = async () => {
			const data = new TableQuery(tableRequestModel);
			return await http.get<TableContent<ContactNotificationHistoryTableVM>>(API.V1.CONTACT.FIND_HISTORY_BY_ID(id, data));
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findAllSavedForCompanyTable(tableRequestModel: TableQuery) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {
		const action = async () => {
			const data = new TableQuery(tableRequestModel);
			return await http.get<TableContent<CompanyContactTableVM>>(API.V1.CONTACT.TABLE_SAVED(data));
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findAllUnsavedForCompanyTable(tableRequestModel: TableQuery) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {
		const action = async () => {
			const data = new TableQuery(tableRequestModel);
			return await http.get<TableContent<CompanyUnsavedContactTableVM>>(API.V1.CONTACT.TABLE_UNSAVED(data));
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function softDeleteContact(id: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {
		const action = async () => {
			await http.delete(API.V1.CONTACT.SOFT_DELETE(id));
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findAllContacts() {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {
		const action = async () => {
			return await http.get<ContactVM[]>(API.V1.CONTACT.FIND_ALL);
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findForInvoices() {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {
		const action = async () => {
			return await http.get<ContactForInvoicesVM[]>(API.V1.CONTACT.FIND_FOR_INVOICES);
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function editContact(form: EditContactAPI.W_Contact_Update_RM, orgAlias: string, companyName: string) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {
		const action = async () => {
			if (!form.id) {
				throw new Error('Contact ID not provided');
			}
			await http.put(EditContactAPI.URL(form.id), form);
			redirectTo(CLIENT.COMPANY.CONTACTS.LIST(orgAlias, companyName));
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function addUnsaved(savedContactId: number, form: MergeContactRM) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {
		const action = async () => {
			await http.put(API.V1.CONTACT.ADD_UNSAVED(savedContactId), form);
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}
